import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Button, FormControl, RadioGroup, Radio, FormControlLabel, Typography } from "@material-ui/core";
import { PlainTextField } from "components";
import { getAliasAccountsAvailable, postAdminAliasAccounts, postAliasAccounts } from "api/alias";
import { useSnackbar } from "notistack";
import { UserProfile } from "api/user";
import config from "config";
import i18n from "./i18n";

// prop types
interface CreateAliasAccountDialogProps {
    clubId?: string,
    fromAdminMenu?: boolean,
    show: boolean,
    userProfile: UserProfile | null,
    onClose: () => void,
    onFetchAliasAccounts: () => void,
}

// embed dashboard dialog
const CreateAliasAccountDialog = ({ clubId, fromAdminMenu, show, userProfile, onClose, onFetchAliasAccounts }: CreateAliasAccountDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.insufficientNumberOfSeatsInCompanyError(error.associated)}`,
        { variant: "error" },
    );

    // ui state
    const [aliasAddress, setAliasAddress] = useState("");
    const [aliasName, setAliasName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [isAvailable, setAvailable] = useState(true);
    const [isLoading, setLoading] = useState(false);
    const [lastName, setLastName] = useState("");
    const [linkWith, setLinkWith] = useState<"user" | "club">(fromAdminMenu ? "club" : "user");
    const isAdmin = ["admin", "owner"].includes(userProfile?.memberships.find((m) => m.club.id === clubId)?.userRole!);

    // fetch availability
    const fetchAvailable = async () => {
        if (aliasName.length > 0) {
            const { available, email } = await getAliasAccountsAvailable({ aliasName });
            setAliasAddress(email);
            setAvailable(available);
        } else {
            setAliasAddress("");
            setAvailable(true);
        }
    };
    useEffect(() => { fetchAvailable(); }, [aliasName]); // eslint-disable-line

    // admin rights
    useEffect(() => { if (!isAdmin) setLinkWith("user"); }, [isAdmin]);

    // create account
    const createAccount = async () => {
        setLoading(true);
        try {
            if (linkWith === "user") {
                await postAliasAccounts({ aliasName, clubId: clubId! });
            } else {
                await postAdminAliasAccounts({ firstName, lastName, clubId: clubId!, emailPrefix: aliasName });
            }
            setAliasAddress("");
            setAliasName("");
            setAvailable(true);
            setFirstName("");
            setLastName("");
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
            onFetchAliasAccounts();
            onClose();
        } catch (error: any) {
            if (error.type === "insufficientNumberOfSeatsInCompany") {
                displayCustomError(error);
            } else {
                displayError(error);
            }
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog open={show} onClose={onClose}>
            <DialogTitle>{i18n.createAliasAccount}</DialogTitle>
            <DialogContent dividers>
                {clubId && (
                    <>
                        {/* link with */}
                        {isAdmin && (
                            <FormControl margin="normal">
                                <RadioGroup
                                    row
                                    value={linkWith}
                                    onChange={(e) => setLinkWith(e.target.value as "user" | "club")}
                                >
                                    <FormControlLabel
                                        control={<Radio color="primary" />}
                                        label={i18n.linkWithUser}
                                        value="user"
                                    />
                                    <FormControlLabel
                                        control={<Radio color="primary" />}
                                        label={i18n.linkWithClub}
                                        value="club"
                                    />
                                </RadioGroup>
                            </FormControl>
                        )}

                        {/* name fields */}
                        {linkWith === "club" && (
                            <>
                                <PlainTextField value={firstName} label={i18n.firstName} onChange={(e) => setFirstName(e.target.value)} />
                                <PlainTextField value={lastName} label={i18n.lastName} onChange={(e) => setLastName(e.target.value)} />
                            </>
                        )}

                        {/* alias name */}
                        <PlainTextField
                            error={linkWith === "user" && !isAvailable}
                            helperText={linkWith === "user" ? (aliasAddress ? i18n.aliasDesc(aliasAddress, userProfile?.email) : "") : (aliasName ? i18n.aliasDesc(`${aliasName}${config.aliasAccount.emailSuffix}`, userProfile?.email) : "")} // eslint-disable-line
                            label={linkWith === "user" ? i18n.name : i18n.emailPrefix}
                            style={{ minWidth: "480px" }}
                            value={aliasName}
                            onChange={(e) => setAliasName(e.target.value)}
                        />
                    </>
                )}

                {/* notice for non-club members */}
                {!clubId && (
                    <Typography variant="subtitle2">{i18n.aliasNonClub}</Typography>
                )}
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                {clubId && (
                    <Button color="primary" onClick={createAccount} disabled={!aliasName || isLoading}>
                        {i18n.save}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateAliasAccountDialog;
